import { useEffect, useRef } from 'react'

/**
 * Captures typed BPMN edit events from a bpmn-js modeler's command stack so they
 * can be flushed alongside a model-deliverable save (attribution + semantic diff).
 *
 * One logical edit aims to map to one event; an on-save integrity self-check
 * (Phase 2, bpmn-js-differ) catches capture drift. Until the model-deliverable
 * cutover is enabled the backend ignores these events, so capturing is harmless.
 */
export type ModelEditOp = 'add' | 'remove' | 'modify' | 'layout'

export interface ModelEditEvent {
	element_id: string
	element_type: string
	op: ModelEditOp
	before?: Record<string, unknown> | null
	after?: Record<string, unknown> | null
	/** 'agent' marks edits applied from an accepted validation-plan card. */
	author_type?: 'user' | 'agent'
	source?: Record<string, unknown>
}

interface EventBusLike {
	on: (event: string, callback: (e: unknown) => void) => void
	off: (event: string, callback: (e: unknown) => void) => void
}

interface ModelerLike {
	get: (service: string) => unknown
}

interface CommandContextElement {
	id?: string
	type?: string
	businessObject?: { name?: string }
}

interface CommandContext {
	shape?: CommandContextElement
	connection?: CommandContextElement
	element?: CommandContextElement
	elements?: CommandContextElement[]
	oldShape?: CommandContextElement
	newShape?: CommandContextElement
	properties?: Record<string, unknown>
	oldProperties?: Record<string, unknown>
	moddleProperties?: Record<string, unknown>
	oldModdleProperties?: Record<string, unknown>
}

type CommandContextField = 'shape' | 'connection' | 'element' | 'elements' | 'replace'

// bpmn-js command-stack event -> our op + which context field holds the element.
const COMMAND_EVENT_MAP: Array<{
	event: string
	op: ModelEditOp
	field: CommandContextField
}> = [
	{ event: 'commandStack.shape.create.executed', op: 'add', field: 'shape' },
	{ event: 'commandStack.shape.append.executed', op: 'add', field: 'shape' },
	{ event: 'commandStack.elements.create.executed', op: 'add', field: 'elements' },
	{ event: 'commandStack.shape.delete.executed', op: 'remove', field: 'shape' },
	{ event: 'commandStack.elements.delete.executed', op: 'remove', field: 'elements' },
	{ event: 'commandStack.connection.create.executed', op: 'add', field: 'connection' },
	{ event: 'commandStack.connection.delete.executed', op: 'remove', field: 'connection' },
	{ event: 'commandStack.shape.move.executed', op: 'layout', field: 'shape' },
	{ event: 'commandStack.elements.move.executed', op: 'layout', field: 'elements' },
	{ event: 'commandStack.connection.updateWaypoints.executed', op: 'layout', field: 'connection' },
	{ event: 'commandStack.connection.layout.executed', op: 'layout', field: 'connection' },
	{ event: 'commandStack.shape.resize.executed', op: 'layout', field: 'shape' },
	{ event: 'commandStack.connection.reconnect.executed', op: 'modify', field: 'connection' },
	{ event: 'commandStack.shape.replace.executed', op: 'modify', field: 'replace' },
	{ event: 'commandStack.element.updateProperties.executed', op: 'modify', field: 'element' },
	{ event: 'commandStack.element.updateModdleProperties.executed', op: 'modify', field: 'element' },
	{ event: 'commandStack.element.updateLabel.executed', op: 'modify', field: 'element' },
]

function toElements(context: CommandContext, field: CommandContextField): CommandContextElement[] {
	if (field === 'elements') return context.elements ?? []
	if (field === 'replace') {
		const elements: CommandContextElement[] = []
		if (context.oldShape?.id) elements.push(context.oldShape)
		if (context.newShape?.id) elements.push(context.newShape)
		return elements
	}
	const el = context[field]
	return el ? [el] : []
}

export interface ModelEditCaptureController {
	/** Return and clear all buffered events (call when flushing a save). */
	drain: () => ModelEditEvent[]
	/** Peek at the current buffer length without clearing. */
	size: () => number
	/** Tag the next buffered events as agent-applied (validation-plan implement). */
	markNextAsAgent: (source: Record<string, unknown>) => void
	detach: () => void
}

interface CommandStackEvent {
	context?: CommandContext
	/** Shared by nested commands in one undo/redo group (diagram-js CommandStack). */
	id?: string | number
}

/** At most one buffered event per element within a command-stack action group. */
function shouldCaptureElement(
	seen: Set<string>,
	actionId: string | number | undefined,
	elementId: string
): boolean {
	if (actionId === undefined || actionId === null) return true
	const key = `${actionId}:${elementId}`
	if (seen.has(key)) return false
	seen.add(key)
	return true
}

export function createModelEditCapture(modeler: ModelerLike): ModelEditCaptureController {
	const eventBus = modeler.get('eventBus') as EventBusLike
	const buffer: ModelEditEvent[] = []
	const seenPerActionElement = new Set<string>()
	let agentSource: Record<string, unknown> | null = null

	const handlers: Array<{ event: string; cb: (e: unknown) => void }> = []

	const pushCaptured = (actionId: string | number | undefined, edit: ModelEditEvent) => {
		if (!shouldCaptureElement(seenPerActionElement, actionId, edit.element_id)) return
		buffer.push(edit)
	}

	const authorFields = () =>
		agentSource
			? { author_type: 'agent' as const, source: agentSource }
			: { author_type: 'user' as const }

	for (const { event, op, field } of COMMAND_EVENT_MAP) {
		const cb = (e: unknown) => {
			try {
				const stackEvent = e as CommandStackEvent
				const context = stackEvent.context
				if (!context) return
				const actionId = stackEvent.id

				if (field === 'replace') {
					if (context.oldShape?.id) {
						pushCaptured(actionId, {
							element_id: context.oldShape.id,
							element_type: context.oldShape.type ?? 'bpmn:FlowElement',
							op: 'remove',
							before: context.oldShape.businessObject?.name
								? { name: context.oldShape.businessObject.name }
								: null,
							after: null,
							...authorFields(),
						})
					}
					if (context.newShape?.id) {
						pushCaptured(actionId, {
							element_id: context.newShape.id,
							element_type: context.newShape.type ?? 'bpmn:FlowElement',
							op: 'add',
							before: null,
							after: context.newShape.businessObject?.name
								? { name: context.newShape.businessObject.name }
								: null,
							...authorFields(),
						})
					}
					return
				}

				for (const element of toElements(context, field)) {
					if (!element?.id) continue
					const beforeProps =
						op === 'modify' ? (context.oldProperties ?? context.oldModdleProperties ?? null) : null
					const afterProps =
						op === 'modify'
							? (context.properties ??
								context.moddleProperties ??
								(element.businessObject?.name ? { name: element.businessObject.name } : null))
							: element.businessObject?.name
								? { name: element.businessObject.name }
								: null
					pushCaptured(actionId, {
						element_id: element.id,
						element_type: element.type ?? 'bpmn:FlowElement',
						op,
						before: beforeProps,
						after: afterProps,
						...authorFields(),
					})
				}
			} catch (err) {
				console.warn('Model edit capture failed for', event, err)
			}
		}
		eventBus.on(event, cb)
		handlers.push({ event, cb })
	}

	return {
		drain: () => {
			const out = buffer.splice(0, buffer.length)
			agentSource = null
			seenPerActionElement.clear()
			return out
		},
		size: () => buffer.length,
		markNextAsAgent: (source) => {
			agentSource = source
		},
		detach: () => {
			for (const { event, cb } of handlers) {
				eventBus.off(event, cb)
			}
		},
	}
}

/**
 * React wrapper: attaches a capture controller to the given modeler and keeps a
 * stable ref the editor can drain on save. Returns the controller ref.
 */
export function useModelEditCapture(modeler: ModelerLike | null) {
	const controllerRef = useRef<ModelEditCaptureController | null>(null)

	useEffect(() => {
		if (!modeler) {
			controllerRef.current = null
			return
		}
		const controller = createModelEditCapture(modeler)
		controllerRef.current = controller
		return () => {
			controller.detach()
			controllerRef.current = null
		}
	}, [modeler])

	return controllerRef
}
